import { ROTULO_DA_CATEGORIA, ROTULO_DA_ETAPA_DE_CUSTO } from '../../constants/insumos';

/**
 * Cada custo, o seu valor e a chave de repasse. Quilometragem tem dois números
 * (km e R$/km); quem decide se ela já foi lançada é o km.
 */
const CUSTOS = [
  { valor: 'mileage_km',    repasse: 'pass_through_mileage' },
  { valor: 'material_cost', repasse: 'pass_through_material' },
  { valor: 'daily_cost',    repasse: 'pass_through_daily' },
  { valor: 'genetics_cost', repasse: 'pass_through_genetics' },
  { valor: 'semen_cost',    repasse: 'pass_through_semen' },
];

const numero = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Como a tela de custos abre.
 *
 * REPASSAR É O PADRÃO, mas só para o custo que ainda não foi lançado. A coluna
 * nasce `false` no banco, então "não repassar" gravado num custo zerado não é
 * decisão de ninguém — é o padrão antigo. Já num custo com valor, o `false` foi
 * escolhido (ou ao menos visto) por quem lançou, e a tela respeita.
 *
 * O R$/km vem do padrão do laboratório enquanto não houver km lançado; com km
 * lançado, vale o do serviço, mesmo que seja zero.
 */
export const custosAoAbrir = (service = {}, precoPorKmPadrao = 0) => {
  const dados = {};
  CUSTOS.forEach(({ valor, repasse }) => {
    const lancado = numero(service[valor]) > 0;
    dados[valor] = numero(service[valor]);
    dados[repasse] = lancado ? !!service[repasse] : true;
  });
  const kmLancado = numero(service.mileage_km) > 0;
  dados.price_per_km = numero(service.price_per_km)
    || (!kmLancado && numero(precoPorKmPadrao)) || 0;
  return dados;
};

/**
 * O que desce para o banco.
 *
 * Campo apagado na tela vira `''`, e `''` numa coluna numérica derruba o
 * UPDATE inteiro ("invalid input syntax for type numeric") — era por isso que
 * os custos não salvavam. Vazio grava zero; texto vira número.
 */
export const custosParaGravar = (dados = {}) => {
  const linha = { price_per_km: numero(dados.price_per_km) };
  CUSTOS.forEach(({ valor, repasse }) => {
    linha[valor] = numero(dados[valor]);
    linha[repasse] = !!dados[repasse];
  });
  return linha;
};


// ===========================================================================
// CUSTOS DETALHADOS
// ===========================================================================
//
// Uma linha por insumo, por etapa, em `services.custos_detalhados`:
//
//   { id, insumo_id, descricao, categoria, etapa, quantidade, unidade,
//     valor_unitario, repassa }
//
// Substitui os cinco campos fixos (km, material, diária, genética, sêmen).
// "Material: R$ 420" não dizia ao cliente o que ele estava pagando, nem à
// equipe, um mês depois, o que tinha sido cobrado.
//
// O SERVIÇO ANTIGO CONTINUA VALENDO PELOS CAMPOS FIXOS enquanto não tiver
// nenhuma linha. Ao abrir os custos dele, os campos viram linhas
// (`linhasDosCamposAntigos`); ao salvar, as linhas passam a valer e os campos
// são zerados (`custosParaGravar`). Sem isso, apagar a última linha faria os
// custos antigos ressuscitarem.

const centavos = (v) => Math.round(numero(v) * 100) / 100;

/** As linhas do serviço, ou `null` quando ele ainda está nos campos fixos. */
export const linhasDoServico = (service = {}) => {
  const linhas = service?.custos_detalhados;
  return Array.isArray(linhas) && linhas.length > 0 ? linhas : null;
};

export const totalDaLinha = (linha = {}) =>
  centavos(numero(linha.quantidade) * numero(linha.valor_unitario));

const novoId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

/** Linha vazia, pronta para preencher. Repassar é o padrão, como antes. */
export const novaLinha = (etapa = 'geral', base = {}) => ({
  id: novoId(),
  insumo_id: null,
  descricao: '',
  categoria: 'outros',
  etapa,
  quantidade: 1,
  unidade: 'un',
  valor_unitario: null,
  repassa: true,
  ...base,
});

/**
 * Os cinco campos fixos de um serviço antigo, como linhas.
 *
 * Só o que tem valor: um serviço sem custo lançado abre vazio, e não com cinco
 * linhas zeradas para apagar. O repasse gravado é respeitado — ali ele foi
 * escolhido por quem lançou.
 */
export const linhasDosCamposAntigos = (service = {}) => {
  const linhas = [];
  const km = numero(service.mileage_km);
  if (km > 0) {
    linhas.push(novaLinha('geral', {
      descricao: 'Quilometragem', categoria: 'deslocamento', unidade: 'km',
      quantidade: km, valor_unitario: numero(service.price_per_km),
      repassa: !!service.pass_through_mileage,
    }));
  }
  [
    ['material_cost', 'pass_through_material', 'Material', 'descartavel'],
    ['daily_cost', 'pass_through_daily', 'Diária', 'diaria'],
    ['genetics_cost', 'pass_through_genetics', 'Genética', 'genetica'],
    ['semen_cost', 'pass_through_semen', 'Sêmen', 'genetica'],
  ].forEach(([valor, repasse, descricao, categoria]) => {
    if (numero(service[valor]) > 0) {
      linhas.push(novaLinha('geral', {
        descricao, categoria, quantidade: 1,
        valor_unitario: numero(service[valor]), repassa: !!service[repasse],
      }));
    }
  });
  return linhas;
};

/** As linhas com que a tela de custos abre. */
export const linhasAoAbrir = (service = {}) =>
  linhasDoServico(service) || linhasDosCamposAntigos(service);

/**
 * O que desce para o banco ao salvar as linhas.
 *
 * Linha sem descrição sai (é a linha em branco que a pessoa adicionou e não
 * usou). Números viram número — `''` numa conta derrubaria o total. E os
 * campos fixos são zerados: daqui em diante quem vale são as linhas.
 */
export const custosParaGravarDetalhados = (linhas = []) => ({
  custos_detalhados: linhas
    .filter(l => (l.descricao || '').trim())
    .map(l => ({
      id: l.id || novoId(),
      insumo_id: l.insumo_id || null,
      descricao: l.descricao.trim(),
      categoria: l.categoria || 'outros',
      etapa: l.etapa || 'geral',
      quantidade: numero(l.quantidade),
      unidade: l.unidade || 'un',
      valor_unitario: l.valor_unitario === null || l.valor_unitario === '' || l.valor_unitario === undefined
        ? null : centavos(l.valor_unitario),
      repassa: !!l.repassa,
    })),
  mileage_km: 0, material_cost: 0, daily_cost: 0, genetics_cost: 0, semen_cost: 0,
  pass_through_mileage: false, pass_through_material: false, pass_through_daily: false,
  pass_through_genetics: false, pass_through_semen: false,
});

/** Custo total e o que vai para o cliente, das linhas que estão na tela. */
export const somaDasLinhas = (linhas = []) => linhas.reduce((acc, l) => {
  const t = totalDaLinha(l);
  return { custo: centavos(acc.custo + t), repasse: centavos(acc.repasse + (l.repassa ? t : 0)) };
}, { custo: 0, repasse: 0 });

/**
 * Total repassado ao cliente.
 *
 * Linhas quando houver; senão, os campos fixos do serviço antigo.
 */
export const calculateOperationalCosts = (service = {}) => {
  const linhas = linhasDoServico(service);
  return linhas ? somaDasLinhas(linhas).repasse : custosAntigos(service);
};

/**
 * O detalhe de cada repasse, para a fatura e para o Financeiro.
 *
 * `type` é a CATEGORIA — é por ela que o Financeiro agrupa (`grupo` é o
 * rótulo). `label` é o item, que é o que a fatura imprime, com quantidade,
 * unidade e etapa ao lado.
 */
export const getOperationalCostsDetails = (service = {}) => {
  const linhas = linhasDoServico(service);
  if (!linhas) {
    return detalhesAntigos(service).map(d => ({ ...d, grupo: d.label }));
  }
  return linhas
    .filter(l => l.repassa && totalDaLinha(l) > 0)
    .map(l => {
      const qtd = numero(l.quantidade);
      const unit = numero(l.valor_unitario);
      return {
        type: l.categoria || 'outros',
        grupo: ROTULO_DA_CATEGORIA[l.categoria] || 'Outros',
        label: l.descricao,
        etapa: ROTULO_DA_ETAPA_DE_CUSTO[l.etapa] || 'Geral',
        quantidade: qtd,
        unidade: l.unidade || 'un',
        valorUnitario: unit,
        description: `${qtd.toLocaleString('pt-BR')} ${l.unidade || 'un'} × R$ ${unit.toFixed(2)}`,
        value: totalDaLinha(l),
      };
    });
};

// ---------------------------------------------------------------------------
// Os campos fixos, para o serviço que ainda não tem linha nenhuma.
// ---------------------------------------------------------------------------

/**
 * Calcula o total de custos operacionais que serão repassados ao cliente
 */
const custosAntigos = (service) => {
  let totalCosts = 0;

  // Quilometragem
  if (service.pass_through_mileage && service.mileage_km && service.price_per_km) {
    totalCosts += (parseFloat(service.mileage_km) || 0) * (parseFloat(service.price_per_km) || 0);
  }

  // Material
  if (service.pass_through_material && service.material_cost) {
    totalCosts += parseFloat(service.material_cost) || 0;
  }

  // Diária
  if (service.pass_through_daily && service.daily_cost) {
    totalCosts += parseFloat(service.daily_cost) || 0;
  }

  // Genética
  if (service.pass_through_genetics && service.genetics_cost) {
    totalCosts += parseFloat(service.genetics_cost) || 0;
  }

  // Sêmen
  if (service.pass_through_semen && service.semen_cost) {
    totalCosts += parseFloat(service.semen_cost) || 0;
  }

  return totalCosts;
};

/**
 * Obtém detalhamento dos custos operacionais
 */
const detalhesAntigos = (service) => {
  const details = [];

  // Quilometragem
  if (service.pass_through_mileage && service.mileage_km && service.price_per_km) {
    const km = parseFloat(service.mileage_km) || 0;
    const pricePerKm = parseFloat(service.price_per_km) || 0;
    const subtotal = km * pricePerKm;
    details.push({
      type: 'mileage',
      label: 'Quilometragem',
      description: `${km.toFixed(0)} km × R$ ${pricePerKm.toFixed(2)}`,
      value: subtotal
    });
  }

  // Material
  if (service.pass_through_material && service.material_cost) {
    const cost = parseFloat(service.material_cost) || 0;
    details.push({
      type: 'material',
      label: 'Material',
      description: 'Materiais utilizados',
      value: cost
    });
  }

  // Diária
  if (service.pass_through_daily && service.daily_cost) {
    const cost = parseFloat(service.daily_cost) || 0;
    details.push({
      type: 'daily',
      label: 'Diária',
      description: 'Custo de diária',
      value: cost
    });
  }

  // Genética
  if (service.pass_through_genetics && service.genetics_cost) {
    const cost = parseFloat(service.genetics_cost) || 0;
    details.push({
      type: 'genetics',
      label: 'Genética',
      description: 'Material genético',
      value: cost
    });
  }

  // Sêmen
  if (service.pass_through_semen && service.semen_cost) {
    const cost = parseFloat(service.semen_cost) || 0;
    details.push({
      type: 'semen',
      label: 'Sêmen',
      description: 'Doses de sêmen',
      value: cost
    });
  }

  return details;
};
